import * as THREE from 'three';

// Renders the UI-inversion regions as outlined rectangles + corner /
// edge handles on top of the active playmat. Coords come in as
// normalized UV (0..1); converted to world via a uvToWorld helper
// the host passes in (tableScene knows the playmat geometry).
//
// Selection rendering: every region gets a faint outline; the active
// region gets a brighter outline plus 8 handles (4 corners + 4 mid-
// edges) the editor uses for resize.

const Y_OUTLINES = 0.18;
const Y_HANDLES = 0.22;

const COLOR_OUTLINE = 0x6ad8a3;
const COLOR_ACTIVE_OUTLINE = 0xa2f0c4;
const COLOR_PREVIEW_OUTLINE = 0xe8d5a0;
const COLOR_HANDLE_CORNER = 0xc0f0d8;
const COLOR_HANDLE_EDGE = 0x80d0a8;

// `dx`/`dy` are 0/0.5/1 multipliers against (width, height) from the
// region's top-left, so the position of each handle scales naturally
// with the region rectangle. `id` is the cardinal direction; the
// editor uses the letter set (n/s/e/w) to pick which sides resize.
const HANDLE_DEFS = [
  { id: 'nw', dx: 0,   dy: 0,   type: 'corner' },
  { id: 'ne', dx: 1,   dy: 0,   type: 'corner' },
  { id: 'se', dx: 1,   dy: 1,   type: 'corner' },
  { id: 'sw', dx: 0,   dy: 1,   type: 'corner' },
  { id: 'n',  dx: 0.5, dy: 0,   type: 'edge' },
  { id: 'e',  dx: 1,   dy: 0.5, type: 'edge' },
  { id: 's',  dx: 0.5, dy: 1,   type: 'edge' },
  { id: 'w',  dx: 0,   dy: 0.5, type: 'edge' },
];

export class UiInversionRenderer {
  constructor({ scene, uvToWorld }) {
    this.scene = scene;
    this.uvToWorld = uvToWorld;
    this.outlines = [];
    this.handles = [];
    this.previewMesh = null;
  }

  update(regions, activeIndex) {
    this.clearOutlines();
    this.clearHandles();
    this.clearPreview();
    if (!Array.isArray(regions)) return;
    for (let i = 0; i < regions.length; i++) {
      const r = regions[i];
      const isActive = i === activeIndex;
      const mesh = this.makeOutline(
        r,
        isActive ? COLOR_ACTIVE_OUTLINE : COLOR_OUTLINE,
        isActive ? 0.9 : 0.5,
      );
      if (mesh) this.outlines.push(mesh);
    }
    if (activeIndex >= 0 && activeIndex < regions.length) {
      this.createHandles(regions[activeIndex], activeIndex);
    }
  }

  previewRect(start, end) {
    this.clearPreview();
    if (!start || !end) return;
    const x = Math.min(start.u, end.u);
    const y = Math.min(start.v, end.v);
    const width = Math.abs(end.u - start.u);
    const height = Math.abs(end.v - start.v);
    if (width < 0.001 || height < 0.001) return;
    this.previewMesh = this.makeOutline(
      { x, y, width, height },
      COLOR_PREVIEW_OUTLINE,
      0.8,
    );
  }

  makeOutline(region, color, opacity) {
    if (!this.scene) return null;
    const { x, y, width, height } = region;
    const tl = this.uvToWorld({ u: x, v: y });
    const tr = this.uvToWorld({ u: x + width, v: y });
    const br = this.uvToWorld({ u: x + width, v: y + height });
    const bl = this.uvToWorld({ u: x, v: y + height });
    const verts = [
      tl.x, Y_OUTLINES, tl.z, tr.x, Y_OUTLINES, tr.z,
      tr.x, Y_OUTLINES, tr.z, br.x, Y_OUTLINES, br.z,
      br.x, Y_OUTLINES, br.z, bl.x, Y_OUTLINES, bl.z,
      bl.x, Y_OUTLINES, bl.z, tl.x, Y_OUTLINES, tl.z,
    ];
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.Float32BufferAttribute(verts, 3));
    const mat = new THREE.LineBasicMaterial({ color, transparent: true, opacity });
    const mesh = new THREE.LineSegments(geo, mat);
    mesh.renderOrder = 50;
    this.scene.scene.add(mesh);
    return mesh;
  }

  createHandles(region, regionIndex) {
    if (!this.scene) return;
    const { x, y, width, height } = region;
    for (const h of HANDLE_DEFS) {
      const u = x + h.dx * width;
      const v = y + h.dy * height;
      const w = this.uvToWorld({ u, v });
      const radius = h.type === 'corner' ? 1.1 : 0.75;
      const geo = new THREE.SphereGeometry(radius, 12, 12);
      const mat = new THREE.MeshBasicMaterial({
        color: h.type === 'corner' ? COLOR_HANDLE_CORNER : COLOR_HANDLE_EDGE,
        transparent: true,
        opacity: 0.95,
        depthTest: false,
      });
      const mesh = new THREE.Mesh(geo, mat);
      mesh.position.set(w.x, Y_HANDLES, w.z);
      mesh.renderOrder = 100;
      mesh.userData = {
        type: 'uiInversionHandle',
        regionIndex,
        corner: h.id,
      };
      this.scene.scene.add(mesh);
      this.handles.push(mesh);
    }
  }

  getHandleMeshes() {
    return this.handles;
  }

  clearOutlines() {
    for (const m of this.outlines) {
      this.scene?.scene.remove(m);
      m.geometry.dispose();
      m.material.dispose();
    }
    this.outlines = [];
  }

  clearHandles() {
    for (const h of this.handles) {
      this.scene?.scene.remove(h);
      h.geometry.dispose();
      h.material.dispose();
    }
    this.handles = [];
  }

  clearPreview() {
    if (this.previewMesh) {
      this.scene?.scene.remove(this.previewMesh);
      this.previewMesh.geometry.dispose();
      this.previewMesh.material.dispose();
      this.previewMesh = null;
    }
  }

  clear() {
    this.clearOutlines();
    this.clearHandles();
    this.clearPreview();
  }

  dispose() {
    this.clear();
  }
}
